import './ProjectDetailsImagesListTable.scss';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { MouseEvent, ReactElement } from 'react';
import { S_Checkbox } from '../../../../../../design/inputs/checkbox/S_Checkbox';
import { S_Link2 } from '../../../../../../design/links/S_Link2';
import { AnnotationStatusBadge } from '../../../../../../modules/common/statusesBadges/annotationStatus/AnnotationStatusBadge';
import { GTable, IGColumnProps } from '../../../../../components/table/GTable';
import { EnableFor } from '../../../../../containers/EnableForRole';
import { toLocaleDateString } from '../../../../../helpers/date.helpers';
import { SortingDirection } from '../../../../../models/sortingDirection.model';
import { Home } from '../../../../../routes/config/Home';
import { ImageSetBadge } from '../../../../annotation/components/ImageSetBadge';
import { useProjectDetailsPermissions } from '../../../projectDetails.permissions';
import { SortingBy } from '../imageFilters.model';
import { ImageSetType, IProjectDetailsImage } from '../projectDetailsImages.model';
import { IProjectDetailsImagesListProps } from './ProjectDetailsImagesList';

interface IImageListItem extends IProjectDetailsImage {
  isChecked: boolean;
}

interface IImagesIGColumnProps<T> extends IGColumnProps<T> {
  sortingBy: SortingBy;
}

export const ProjectDetailsImagesListTable = observer((props: IProjectDetailsImagesListProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useProjectDetailsPermissions();
  const canSeeUsers = permissions.canSeeAnnotationAuthors();
  const canUpdateImageSet = permissions.canUpdateImageSet();
  const canBatchAnswerQuestions = permissions.canBatchAnswer();
  const canSelectImages = canBatchAnswerQuestions.allowed;

  const onSortChanged = async (orderBy: string, orderType: string): Promise<void> => {
    const by = gColumns.find((x) => x.sortBy === orderBy)?.sortingBy;
    const direction: SortingDirection | undefined = Object.values(SortingDirection).find((x) => x.toLocaleLowerCase() === orderType.toLocaleLowerCase());

    if (by) {
      await props.onOrderingChange(by, direction);
    }
  };

  const getNavigationUrl = (imageId: string): string =>
    Home.FreeAccess.View.withParams({ imageId, workspaceId: props.workspaceId, projectId: props.projectId, filterId: props.filterId });

  const renderName = (image: IImageListItem): ReactElement => (
    <S_Link2
      color='text'
      title={image.name}
      to={getNavigationUrl(image.id)}
    >
      {image.name}
    </S_Link2>
  );

  const renderDatasetName = (image: IImageListItem): ReactElement => {
    const showWarning = image.datasetStatus !== 'Published' && image.datasetStatus !== 'PublishedUploading';

    return (
      <div>
        {showWarning && (
          <span className='unpublished-dataset-icon'>
            <FontAwesomeIcon
              title={t('unpublished')}
              icon={faExclamationTriangle}
            />
            &nbsp;
          </span>
        )}
        <span>{image.datasetName}</span>
      </div>
    );
  };

  const renderWasImageCorrected = (image: IImageListItem): ReactElement => <span>{image.wasImageCorrected ? t('yes') : t('no')}</span>;

  const renderDateOfMarking = ({ dateOfMarking }: IImageListItem): ReactElement => <span>{dateOfMarking && toLocaleDateString(dateOfMarking)}</span>;

  const renderDateOfApproval = ({ dateOfApproval }: IImageListItem): ReactElement => <span>{dateOfApproval && toLocaleDateString(dateOfApproval)}</span>;

  const renderStatus = (image: IImageListItem): ReactElement => {
    return <AnnotationStatusBadge annotationStatus={image.status} />;
  };

  const renderCheckbox = (item: IImageListItem): ReactElement => (
    <div
      className='image-list-checkbox'
      title={item.isLocked ? t('someone_is_working') : ''}
    >
      {canSelectImages && (
        <S_Checkbox
          checked={item.isChecked}
          onClick={(e) => {
            handleCheckboxClick(e, item);
          }}
          value={item.id}
          id={item.id}
          disabled={item.isLocked}
          className={classNames({ locked: item.isLocked })}
        />
      )}
    </div>
  );

  const renderSelectHeader = (): ReactElement => (
    <div className='image-list-checkbox header'>
      {canSelectImages && (
        <S_Checkbox
          checked={props.selectionMode === 'Deselect'}
          onClick={props.onCheckAll}
          value={'id-1'}
          id={'id-1'}
        />
      )}
    </div>
  );

  const renderImageSet = (item: IImageListItem): ReactElement => {
    return (
      <EnableFor hasPermission={canUpdateImageSet}>
        <ImageSetBadge
          set={item.imageSet}
          onChange={async (value) => {
            await props.onImageSetChange(item.id, value ?? ImageSetType.NotSet);
          }}
        />
      </EnableFor>
    );
  };

  const renderWorkTime = (item: IImageListItem): ReactElement => <>{removeFractionPartFromTimeSpan(item.workTime)}</>;

  const handleCheckboxClick = (e: MouseEvent, image: IImageListItem): void => {
    if (canSelectImages && !image.isLocked) {
      props.onCheck(image.id, e.nativeEvent.shiftKey);
    }
  };

  const removeFractionPartFromTimeSpan = (timeSpan: string): string => {
    return timeSpan.includes('.') ? timeSpan.substring(0, timeSpan.lastIndexOf('.')) : timeSpan;
  };

  const gColumns: IImagesIGColumnProps<IImageListItem>[] = [];

  if (canBatchAnswerQuestions.allowed) {
    gColumns.push({
      headerName: '',
      renderer: renderCheckbox,
      headerRenderer: renderSelectHeader,
      sortingBy: SortingBy.None,
    });
  }

  gColumns.push(
    {
      sortBy: 'name',
      headerName: t('project_details_images.header_name'),
      renderer: renderName,
      sortingBy: SortingBy.Name,
    },
    {
      sortBy: 'datasetName',
      headerName: t('project_details_images.header_datasetName'),
      renderer: renderDatasetName,
      sortingBy: SortingBy.DatasetName,
    },
    {
      sortBy: 'status',
      headerName: t('project_details_images.header_status'),
      renderer: renderStatus,
      sortingBy: SortingBy.Status,
    }
  );

  if (canSeeUsers.allowed) {
    gColumns.push(
      {
        sortBy: 'annotatedBy',
        headerName: t('project_details_images.header_annotatedBy'),
        renderer: (x) => x.annotatedBy,
        sortingBy: SortingBy.AnnotatedBy,
      },
      {
        sortBy: 'reviewedBy',
        headerName: t('project_details_images.header_reviewedBy'),
        renderer: (x) => x.reviewedBy,
        sortingBy: SortingBy.ReviewedBy,
      }
    );
  }

  gColumns.push({
    sortBy: 'dateOfMarking',
    headerName: t('project_details_images.header_dateOfMarking'),
    sortingBy: SortingBy.DateOfMarking,
    renderer: renderDateOfMarking,
  });

  gColumns.push({
    sortBy: 'dateOfApproval',
    headerName: t('project_details_images.header_dateOfApproval'),
    sortingBy: SortingBy.DateOfApproval,
    renderer: renderDateOfApproval,
  });

  gColumns.push({
    sortBy: 'wasImageCorrected',
    headerName: t('project_details_images.header_wasImageCorrected'),
    headerTooltip: t('corrected_during_review'),
    renderer: renderWasImageCorrected,
    sortingBy: SortingBy.WasImageCorrected,
  });

  gColumns.push({
    sortBy: 'imageSet',
    headerName: t('project_details_images.header_imageSet'),
    renderer: renderImageSet,
    sortingBy: SortingBy.ImageSet,
    cellClassName: 'image-set-column',
  });

  gColumns.push({
    sortBy: 'workTime',
    headerName: t('project_details_images.header_workTime'),
    renderer: renderWorkTime,
    sortingBy: SortingBy.WorkTime,
  });

  const images: IImageListItem[] = props.detailsImages.map((i) => {
    const isInList = props.toggledImages.some((id) => id === i.id);

    return {
      ...i,
      isChecked: !i.isLocked && ((isInList && props.selectionMode === 'Select') || (!isInList && props.selectionMode === 'Deselect')),
    };
  });

  return (
    <GTable<IImageListItem>
      isLoading={props.isLoading}
      columns={gColumns}
      noItemsPlaceholder={t('please_assign_datasets')}
      items={images}
      paginationProps={props.pagination}
      sortingModel={[{ orderBy: props.orderBy, orderType: props.orderType }]}
      onSortChanged={onSortChanged}
    />
  );
});
